import { Box, Button, Chip, Grid, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext, SubscriptionTab } from "contexts/Subscription";
import { useContext } from "react";
import { SubscriptionType } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { useSubscription } from "utils/useSubscription";

import {
  PlanAction,
  getCurrentPlanButton,
  getPlanLimitText,
  isCurrentPlan,
} from "../helpers";
import styles from "./styles";

interface Props {
  plan: Plans;
}

const ConciergePlanCard = ({ plan }: Props) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { plans, emailSents, subscription, tab } =
    useContext(SubscriptionContext);
  const { onPlanButtonClick } = useSubscription();

  const planName = plans?.[plan]?.display_name;
  const action = getCurrentPlanButton(
    plan,
    subscription?.subscription?.planName || Plans.FREE,
    subscription,
  );

  const BENEFITS = [
    {
      icon: "⚡",
      primary: (
        <>
          <strong>300 Automated Pitches / Month</strong>: Start building your
          network of brand contacts with a steady stream of pitches - designed
          to help you land your first collaborations confidently.
        </>
      ),
    },
    {
      icon: "📚",
      primary: (
        <>
          <strong>Brand Partnership Strategy Plan</strong> - A personalized plan
          that gives you a clear roadmap from where you are today to landing
          consistent, paid brand deals.
        </>
      ),
    },
    {
      icon: "☎️",
      primary: (
        <>
          <strong>Monthly Group Coaching & Community</strong>: Access to live
          Q&A and growth strategy sessions with a Creator Growth coach. Learn
          from peers and get tactical tips to build momentum.
        </>
      ),
    },
    {
      icon: "📧",
      primary: (
        <>
          <strong>Brand Deal Helper:</strong> We'll help you craft replies to
          brands, set your rates, and close your deals with negotiation help.
        </>
      ),
    },
  ];

  return (
    <>
      <Box sx={styles.card}>
        <Box sx={[styles.planTitleCard, { backgroundColor: "green.dark" }]}>
          <Grid container justifyContent="flex-end">
            <Chip
              sx={{ backgroundColor: "white", px: 1 }}
              label={
                <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                  <strong>Kickstart your creator journey</strong>
                </Typography>
              }
            ></Chip>
          </Grid>

          <Typography variant="h3" sx={{ mt: 6, color: "white" }}>
            {planName}{" "}
            {isCurrentPlan(subscription, plan) &&
              subscription?.subscription?.status ===
                SubscriptionType.ACTIVE && (
                <Box
                  component="span"
                  sx={{ color: "purple.main", fontSize: 16 }}
                >
                  (Current Plan)
                </Box>
              )}
          </Typography>
          <Box sx={styles.price}>
            {tab === SubscriptionTab.yearly && (
              <>
                <Box sx={styles.strike}>
                  <Box>${plans && plans[Plans.CONCIERGE]?.price}/mo</Box>
                </Box>
                <Box>${plans && plans[Plans.CONCIERGE_YEARLY]?.price}/mo</Box>
              </>
            )}
            {tab === SubscriptionTab.monthly && (
              <Box>${plans && plans[Plans.CONCIERGE]?.price}/mo</Box>
            )}
          </Box>

          <Box sx={{ fontSize: 14, color: "yellow.main" }}>
            {plans && plans[Plans.CONCIERGE]?.price_annotation}
          </Box>
        </Box>

        {BENEFITS.map((benefit, index) => (
          <Grid container key={index} alignItems="center" sx={{ mb: 1.5 }}>
            <Grid
              container
              justifyContent="left"
              alignItems="center"
              item
              xs="auto"
            >
              <Box sx={styles.iconBlock}>{benefit.icon}</Box>
            </Grid>
            <Grid item xs md>
              <Typography component="div">{benefit.primary}</Typography>
            </Grid>
          </Grid>
        ))}
        {!currentOrg?.overridePlanName && (
          <Button
            disableElevation
            fullWidth
            variant={action === PlanAction.upgrade ? "contained" : "text"}
            sx={{
              mt: 3,
              py: 1,
              borderRadius: 2,
              backgroundColor:
                action === PlanAction.upgrade ? "primary.main" : "#f4f8f6",
            }}
            onClick={() => onPlanButtonClick(plan, action)}
          >
            {action}
          </Button>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        {getPlanLimitText(subscription, plan, emailSents)}
      </Box>
    </>
  );
};

export default ConciergePlanCard;
